import type { OrganizationResponseV20241120 } from '@gm-commercial/rtk-clients/organizations/types';

export const HEAP_EVENTS = {
  CSP_APP_ENTRY: 'CSP App Entry',
  NOTIFICATION_LINK_CLICKED: 'Notification Link Clicked',
  NOTIFICATION_POPOVER_VIEWED: 'Notification Popover Viewed',
  ORGANIZATION_MANUALLY_APPROVED: 'Organization Manually Approved',
  REMOTE_COMMAND_ACTIVATED: 'Remote Command Activated',
  ACTIVATION_RATE_VEHICLE_MANUALLY_ADDED: 'Vehicle Manually Added',
  ACTIVATION_RATE_VEHICLE_AUTOMATICALLY_ADDED: 'Vehicle Automatically Added',
  ACTIVATION_RATE_USER_MANUALLY_ADDED: 'User Manually Added',
  ACTIVATION_RATE_ORDER_MANUALLY_ADDED: 'Order Manually Added',
  ACTIVATION_RATE_ORDER_AUTOMATICALLY_ADDED: 'Order Automatically Added',
  ACTIVATION_RATE_CAP_VIEWED: 'CAP Viewed',
  MOBILE_SESSION_START: 'Mobile Session Start',
  WEB_SESSION_START: 'Web Session Start',
  PWA_SESSION_START: 'PWA Session Start',
} as const;

export type ActivationRateEvent =
  | typeof HEAP_EVENTS.ACTIVATION_RATE_VEHICLE_AUTOMATICALLY_ADDED
  | typeof HEAP_EVENTS.ACTIVATION_RATE_USER_MANUALLY_ADDED
  | typeof HEAP_EVENTS.ACTIVATION_RATE_ORDER_MANUALLY_ADDED
  | typeof HEAP_EVENTS.ACTIVATION_RATE_ORDER_AUTOMATICALLY_ADDED;

export type ActivationRateProperties = {
  AmountAdded: number;
  AmountFailedToAdd?: number;
  OrgId: string;
};

export const HEAP_VEHICLE_ADD_TYPES = {
  MANUAL: 'MANUAL',
  BULK_UPLOAD: 'BULK_UPLOAD',
} as const;

export type HeapVehicleAddType =
  (typeof HEAP_VEHICLE_ADD_TYPES)[keyof typeof HEAP_VEHICLE_ADD_TYPES];

export type VehicleManuallyAddedProperties = {
  AmountAdded: number;
  OrgId: string;
  vehicleAddType: HeapVehicleAddType;
};

/**
 * How the user reached a tracked page. Navigations carry one in router state;
 * a page reached without it (typed URL, bookmark, untagged link) is `DIRECT_LINK`.
 */
export const HEAP_ENTRY_POINTS = {
  DRIVER_SCORES_LIST: 'driver_scores_list',
  DRIVER_DETAIL: 'driver_detail',
  DRIVER_PERFORMANCE_VIEW_PROFILE: 'driver_performance_view_profile',
  DRIVERS_LIST: 'drivers_list',
  EVENT_HISTORY: 'event_history',
  EVENT_DETAIL: 'event_detail',
  TRIP_HISTORY: 'trip_history',
  TRIP_DETAIL: 'trip_detail',
  NOTIFICATION_FEED: 'notification_feed',
  DIRECT_LINK: 'direct_link',
} as const;

export type HeapEntryPoint =
  (typeof HEAP_ENTRY_POINTS)[keyof typeof HEAP_ENTRY_POINTS];

/** Surface the signed-in user is on, sent to Heap as `platform`. */
export const HEAP_PLATFORMS = {
  WEB: 'Web',
  PWA: 'PWA',
  MOBILE: 'Mobile',
} as const;

export type HeapPlatform = (typeof HEAP_PLATFORMS)[keyof typeof HEAP_PLATFORMS];

/** Session start event for the surface the user opened the app on. */
export const SESSION_START_EVENT_BY_PLATFORM = {
  [HEAP_PLATFORMS.MOBILE]: HEAP_EVENTS.MOBILE_SESSION_START,
  [HEAP_PLATFORMS.WEB]: HEAP_EVENTS.WEB_SESSION_START,
  [HEAP_PLATFORMS.PWA]: HEAP_EVENTS.PWA_SESSION_START,
} as const;

/** Router state a navigation passes so the destination can attribute its Heap events. */
export type HeapNavigationState = {
  entryPoint?: HeapEntryPoint;
  listSortColumn?: string;
  driverScoreId?: string;
  activeFilterState?: Record<string, unknown>;
};

export type HeapOrganizationDetails = Pick<
  OrganizationResponseV20241120,
  'id' | 'name' | 'fan' | 'verificationStatus' | 'orgType'
>;

export type CspHeapContext = {
  organizationId?: string;
  fleetId?: string;
  path?: string;
};
